import React from 'react'
import { Card, Heading } from '../ui'

/**
 * Props interface for the SkillsSection component
 */
export interface SkillsSectionProps {
  /** Unique identifier for the section */
  id?: string
  /** CSS class name for custom styling */
  className?: string
}

/**
 * Skills data structured by category
 */
const SKILLS_DATA = {
  architecture: {
    title: 'Architecture & Systems',
    skills: [
      'Microservices',
      'SOA',
      'Event-Driven Architecture',
      'Real-time Systems',
      'Distributed Systems',
      'High-Concurrency',
      'Database Design',
      'Message Queues',
      'API Design',
      'Security & Compliance',
    ],
  },
  cloud: {
    title: 'Cloud & Infrastructure',
    skills: [
      'Kubernetes',
      'Docker',
      'Istio',
      'Azure',
      'AWS',
      'PostgreSQL',
      'MongoDB',
      'Redis',
      'Containerization',
      'Observability',
      'Chaos Engineering',
    ],
  },
  ai: {
    title: 'AI & Automation',
    skills: [
      'LLM Integration',
      'DevOps Automation',
      'Prompt Engineering',
      'MCP',
      'A2A',
      'RAG',
      'Machine Learning',
      'AI-Powered Workflows',
      'Automated Testing',
      'Release Pipelines',
    ],
  },
  languages: {
    title: 'Languages & Frameworks',
    skills: [
      'TypeScript',
      'JavaScript',
      'Python',
      'C#',
      'C++',
      'React',
      'Node.js',
      '.NET',
      'Unity',
      'Unreal Engine',
    ],
  },
} as const

/**
 * Skills section component showcasing technical expertise and technology stack.
 *
 * Features:
 * - Categorized skill display with interactive tags
 * - Technology keywords for SEO optimization
 * - Responsive grid layout
 * - Accessibility compliant with ARIA labels
 * - Hover interactions for skill tags
 */
export const SkillsSection: React.FC<SkillsSectionProps> = ({
  id,
  className = '',
}) => {
  return (
    <section
      className={`about-section ${className}`}
      aria-labelledby="skills-heading"
      id={id}
    >
      <Heading
        as="h2"
        size="xl"
        variant="default"
        className="section-title"
        id="skills-heading"
      >
        Technical Expertise
      </Heading>

      <div className="skills-grid">
        {Object.entries(SKILLS_DATA).map(([key, category]) => (
          <Card
            key={key}
            variant="glass"
            padding="lg"
            className="skill-category"
          >
            <Heading as="h3" size="lg" variant="default">
              {category.title}
            </Heading>
            <div
              className="skill-tags"
              role="list"
              aria-label={`${category.title} skills`}
            >
              {category.skills.map(skill => (
                <span
                  key={skill}
                  className="skill-tag"
                  role="listitem"
                  tabIndex={0}
                  aria-label={`${skill} technology`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      // Could add skill detail popup or link functionality here
                    }
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </section>
  )
}
